import React, { useEffect, useRef, useState } from 'react';

import { focusRef } from '../shared/EventHelpers';
import { getIcon } from '../shared/FormatHelpers';
import OnHoverTooltip from '../../../../tables/shared/OnHoverTooltip';

const OptionButton = ({ icon, name, isActive, onClick, onKeyDown, isTabbable, shouldFocus, setShouldFocus }) => {
    const ref = useRef();

    useEffect(() => {
        if (shouldFocus && isActive) {
            setShouldFocus(false);
            focusRef(ref);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldFocus]);

    const className = `btn btn-${isActive ? 'secondary' : 'light'}`;
    const buttonProps = { ref, role: 'button', type: 'button', className, onClick, onKeyDown };
    let Icon;
    if (icon !== undefined) {
        if (icon.includes('oi-')) {
            Icon = <div className={`oi ${icon}`} />;
        } else {
            Icon = getIcon(icon);
        }
    } else {
        Icon = getIcon('unknown');
    }

    return (
        <OnHoverTooltip isSlimmed delay={{ show: 50, hide: 50 }} tooltipPlace='top'>
            <button tabIndex={isTabbable ? null : '-1'} {...buttonProps}>
                {Icon}
            </button>
            <span>{name}</span>
        </OnHoverTooltip>
    );
};

/**
 * Button group with icons and tooltip.
 * @param {string} [size] 'sm' or 'lg'. Default is normal.
 * @param {list} [options] [{any id, string name, string icon}, ...] where icon is from oi-library.
 */
const EditType = ({ value, setValue, options, onKeyDown: _onKeyDown, isInvalid, autoFocus, size }) => {
    const [shouldFocus, setShouldFocus] = useState(false);

    const sortOptions = () => {
        let sortOrderByID = ['BOM', 'COM', 'TOD', 'YIA'];
        options = options.sort((b, a) => sortOrderByID.indexOf(b.id) - sortOrderByID.indexOf(a.id));
    };

    useEffect(() => {
        if (autoFocus && value?.id) {
            setShouldFocus(true);
        }
        
        sortOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortOptions]);

    const goRight = () => {
        const currentIndex = options.findIndex((o) => o.id === value.id);
        if (currentIndex < options.length - 1) {
            setValue(options[currentIndex + 1]);
        }
        setShouldFocus(true);
    };
    const goLeft = () => {
        const currentIndex = options.findIndex((o) => o.id === value.id);
        if (currentIndex > 0) {
            setValue(options[currentIndex - 1]);
        }
        setShouldFocus(true);
    };

    if (!options || !options.length) {
        return 'Inga typealternativ givna';
    }

    const onKeyDown = (event) => {
        _onKeyDown && _onKeyDown(event);
        // Right arrow
        if (event.keyCode === 39) {
            goRight();
            event.preventDefault();
        }
        // Left arrow
        if (event.keyCode === 37) {
            goLeft();
            event.preventDefault();
        }
    };

    return (
        <div className='crm-editable-field-wrapper'>
            <div className={`btn-group d-block ${size ? 'btn-group-' + size : ''} edit-type ${isInvalid ? 'invalid' : ''}`} role='group'>
                {options.map((o) => (
                    <OptionButton
                        key={o.id ?? o.name}
                        icon={o.icon}
                        name={o.name}
                        isTabbable={value ? o.id === value.id : o.id === options[0].id}
                        isActive={value?.id === o.id}
                        onClick={() => setValue(o)}
                        {...{ onKeyDown, shouldFocus, setShouldFocus }}
                    />
                ))}
            </div>
        </div>
    );
};

export default EditType;
